import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Pause, Play, ArrowRight, Award, BadgeCheck, Clock, CreditCard, FileText, Headset, Layers, ListChecks, Package, ShieldCheck, Tag, Truck, Wrench } from "lucide-react";
import { mediaVariant } from "../lib/catalogueApi";
import { HeroCountdown } from "./HeroCountdown";

// T61: full-bleed photo banner (user's reference). variant: "auto" = responsive; "desktop" / "tablet" / "phone" = forced
// layout for the admin live previews. A navy fallback paints first; the photo is the LCP element (eager, high priority).
export const HERO_ICON = { Tag, FileText, Layers, Headset, Truck, ShieldCheck, BadgeCheck, Clock, CreditCard, Package, Wrench, Award };
const NAVY = "8,20,45";
const W = [640, 960, 1280, 1600, 1920];
const srcset = (id) => W.map((w) => `${mediaVariant(id, w, "webp")} ${w}w`).join(", ");
const pos = (f) => `${f?.x ?? 50}% ${f?.y ?? 50}%`;
// "laptop" = the 1024px desktop preview (mid-size desktop sizes)
const pick = (v, auto, d, t, p, m = d) => (v === "desktop" ? d : v === "laptop" ? m : v === "tablet" ? t : v === "phone" ? p : auto);
export const shadeAlpha = (s) => Math.max(0, Math.min(100, s ?? 78)) / 100;
// Text-side overlays: desktop = left shade; tablet = stronger; phone = top-heavy.
const grad = (k, a) => ({
  desktop: `linear-gradient(90deg, rgba(${NAVY},${Math.min(1, a + 0.06)}) 0%, rgba(${NAVY},${a}) 38%, rgba(${NAVY},${a * 0.45}) 62%, rgba(${NAVY},0.08) 100%)`,
  tablet: `linear-gradient(90deg, rgba(${NAVY},${Math.min(1, a + 0.12)}) 0%, rgba(${NAVY},${Math.min(1, a + 0.04)}) 60%, rgba(${NAVY},${a * 0.7}) 100%)`,
  phone: `linear-gradient(180deg, rgba(${NAVY},${Math.min(1, a + 0.1)}) 0%, rgba(${NAVY},${a}) 55%, rgba(${NAVY},${a * 0.55}) 80%, rgba(${NAVY},${Math.min(1, a + 0.05)}) 100%)`,
}[k]);

// T62: background video. The poster <img> paints first (LCP); the video mounts after load, only when motion is welcome,
// pauses off-screen / in a hidden tab. T64: no visitor pause control (the user's explicit choice; see the T64 report re WCAG 2.2.2).
// T71: the user wants the hero video to ALWAYS play, so the reduced-motion / Save-Data / 2g gating is removed (a WCAG trade-off at the user's request; the pause button stays).
const motionOk = () => true;
const playMuted = (v) => { v.muted = true; v.defaultMuted = true; v.setAttribute("muted", ""); v.playsInline = true; return v.play(); };

export function useHeroVideo(bg, variant, secRef, active = true) {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  const [paused, setPaused] = useState(false);
  const user = useRef(false);
  const isVideo = bg?.kind === "video" && bg.desktop_video_id;
  const phone = variant === "phone" || (variant === "auto" && typeof window !== "undefined" && window.matchMedia?.("(max-width: 767px)").matches);
  const src = isVideo ? `${process.env.REACT_APP_BACKEND_URL}/api/media/${phone ? bg.phone_video_id : bg.desktop_video_id}` : null;
  useEffect(() => {
    if (!src || !motionOk()) return undefined;
    // start only once the poster has loaded and painted, so the poster stays the LCP element
    let t;
    let f;
    let img;
    let po;
    let started = false;
    const start = () => { if (!started) { started = true; t = setTimeout(() => setOn(true), 300); } };
    const go = () => {  // wait until the browser has reported the poster as the LCP (fallback 2.5s), then start
      try {
        po = new PerformanceObserver((l) => { if (l.getEntries().some((e) => e.element === img)) start(); });
        po.observe({ type: "largest-contentful-paint", buffered: true });
      } catch { /* no LCP API: the timeout starts it */ }
    };
    const wait = () => {
      f = setTimeout(start, 2500);  // fallback also covers a lazy/offscreen poster that never fires load
      img = secRef.current?.querySelector('[data-testid="hero-bg-image"]');
      if (!img || (img.complete && img.naturalWidth)) go(); else img.addEventListener("load", go, { once: true });
    };
    if (document.readyState === "complete") wait(); else window.addEventListener("load", wait, { once: true });
    const hard = setTimeout(start, 4000);  // T71: never wait on window "load" forever (a slow resource used to block the video)
    return () => { clearTimeout(t); clearTimeout(f); clearTimeout(hard); po?.disconnect(); window.removeEventListener("load", wait); img?.removeEventListener("load", go); };
  }, [src, secRef]);
  useEffect(() => {
    const v = ref.current;
    if (!on || !v) return undefined;
    let seen = true;
    const want = () => active && seen && !document.hidden && !user.current;
    const sync = () => { if (want()) playMuted(v).catch(() => {}); else v.pause(); };  // T65: only the active slide plays
    const io = new IntersectionObserver(([e]) => { seen = e.isIntersecting; sync(); });
    io.observe(v);
    if (!active) v.pause();
    // T71: retry when the browser blocked autoplay (low-power modes etc.): on canplay, tab shown, and the first interaction
    const GESTURES = ["pointerdown", "touchstart", "keydown", "scroll", "wheel"];
    const gesture = () => { if (want() && v.paused) playMuted(v).catch(() => {}); if (!v.paused) GESTURES.forEach((g) => window.removeEventListener(g, gesture)); };
    GESTURES.forEach((g) => window.addEventListener(g, gesture, { passive: true }));
    v.addEventListener("canplay", sync);
    let tries = 0;
    const onErr = () => { if (tries++ < 3) setTimeout(() => { v.load(); sync(); }, 800 * tries); };  // T71: transient media errors reload + retry
    v.addEventListener("error", onErr);
    document.addEventListener("visibilitychange", sync);
    const pl = () => setPaused(false), pa = () => setPaused(true);
    v.addEventListener("play", pl); v.addEventListener("pause", pa);
    sync();
    return () => { io.disconnect(); document.removeEventListener("visibilitychange", sync); v.removeEventListener("canplay", sync); v.removeEventListener("error", onErr); GESTURES.forEach((g) => window.removeEventListener(g, gesture));
      v.removeEventListener("play", pl); v.removeEventListener("pause", pa); };
  }, [on, src, active]);
  const toggle = () => { const v = ref.current; if (!v) return; user.current = !v.paused; if (v.paused) playMuted(v).catch(() => {}); else v.pause(); };
  return { on: on && !!src, src, ref, paused, toggle, active, focal: phone ? bg?.phone_focal : bg?.desktop_focal };
}

function HeroVideoEl({ vid }) {
  const [shown, setShown] = useState(false);
  const [armed, setArmed] = useState(vid.active !== false);  // T71: a carousel slide's video mounts (and downloads) only once that slide is shown
  useEffect(() => { if (vid.active !== false) setArmed(true); }, [vid.active]);
  if (!vid.on || !armed) return null;
  // 1px inset keeps the poster (same frame, underneath) the largest paint, so LCP stays on the poster
  return <video ref={vid.ref} src={vid.src} muted loop playsInline autoPlay={vid.active !== false} preload="auto" aria-hidden="true" tabIndex={-1} onPlaying={() => setShown(true)}
    className={`absolute left-px top-px h-[calc(100%-2px)] w-[calc(100%-2px)] object-cover transition-opacity duration-700 ${shown ? "opacity-100" : "opacity-0"}`} style={{ objectPosition: pos(vid.focal) }} data-testid="hero-bg-video" />;
}


function Bg({ bg, variant, vid }) {
  if (bg?.kind !== "photo" && bg?.kind !== "video") return null;
  const a = shadeAlpha(bg.shade);
  const img = (id, f, cls) => <img src={mediaVariant(id, 1280, "webp")} srcSet={srcset(id)} sizes="100vw" alt="" width="1264" height="720" loading="eager" fetchpriority="high"
    decoding="async" className={`absolute inset-0 h-full w-full object-cover ${cls}`} style={{ objectPosition: pos(f) }} data-testid="hero-bg-image" />;
  if (variant !== "auto") {
    const k = variant;
    const ph = k === "phone";
    return (<>
      <div className={`absolute inset-0 ${k === "desktop" || k === "laptop" ? "hero-imgbox-desktop" : ""}`}>{img(ph ? bg.phone_media_id : bg.desktop_media_id, ph ? bg.phone_focal : bg.desktop_focal, "")}<HeroVideoEl vid={vid} /></div>
      <div aria-hidden="true" className="absolute inset-0" style={{ background: grad(k === "laptop" ? "desktop" : k, a) }} data-testid="hero-shade" />
    </>);
  }
  return (<>
    <div className="hero-imgbox-auto absolute inset-0"><picture>
      <source media="(max-width: 767px)" srcSet={srcset(bg.phone_media_id)} sizes="100vw" />
      <img src={mediaVariant(bg.desktop_media_id, 1280, "webp")} srcSet={srcset(bg.desktop_media_id)} sizes="100vw" alt="" width="1264" height="720" loading="eager" fetchpriority="high" decoding="async"
        className="absolute inset-0 h-full w-full object-cover [object-position:var(--fp)] md:[object-position:var(--fd)]" style={{ "--fp": pos(bg.phone_focal), "--fd": pos(bg.desktop_focal) }} data-testid="hero-bg-image" />
    </picture><HeroVideoEl vid={vid} /></div>
    <div aria-hidden="true" className="absolute inset-0 hidden lg:block" style={{ background: grad("desktop", a) }} data-testid="hero-shade" />
    <div aria-hidden="true" className="absolute inset-0 hidden md:block lg:hidden" style={{ background: grad("tablet", a) }} />
    <div aria-hidden="true" className="absolute inset-0 md:hidden" style={{ background: grad("phone", a) }} />
  </>);
}

const AVOID_UNKNOWN = [{ x: 0.4, y: 0, w: 0.6, h: 1 }];  // a photo without marked equipment: keep the note off its right 60%
const hit = (a, b) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
const ANG = (6 * Math.PI) / 180;

// The note places itself in clear space beside the text, never over the photo's equipment (or hides if none fits).
function placeNote(el, bg, scales) {
  const sec = el.closest('[data-testid="home-hero"]');
  const img = sec?.querySelector('[data-testid="hero-bg-image"]');
  if (!el?.offsetWidth || !img?.naturalWidth) return null;
  const S = sec.getBoundingClientRect();
  const k = S.width / sec.offsetWidth || 1;
  const rel = (r) => ({ x: (r.left - S.left) / k, y: (r.top - S.top) / k, w: r.width / k, h: r.height / k });
  const B = rel(img.getBoundingClientRect());
  const c = Math.max(B.w / img.naturalWidth, B.h / img.naturalHeight);
  const rw = img.naturalWidth * c, rh = img.naturalHeight * c;
  const f = bg.desktop_focal || { x: 50, y: 50 };
  const ox = B.x + ((B.w - rw) * f.x) / 100, oy = B.y + ((B.h - rh) * f.y) / 100;
  const av = bg.kind === "video" ? bg.video_avoid : bg.desktop_avoid;  // T62: per-asset areas (resolved server-side or from cfg.media_avoid in admin)
  const avoid = (av?.length ? av : AVOID_UNKNOWN).map((a) => ({ x: ox + a.x * rw - 12, y: oy + a.y * rh - 12, w: a.w * rw + 24, h: a.h * rh + 24 }));
  // the real ink of the text (each headline / supporting-text line, eyebrow, buttons), not the 600px column box
  const ink = [];
  sec.querySelectorAll('[data-testid="hero-headline"], [data-testid="hero-sub"]').forEach((e) => {  // text nodes only: line spans are full-width blocks
    const tw = document.createTreeWalker(e, NodeFilter.SHOW_TEXT);
    for (let n = tw.nextNode(); n; n = tw.nextNode()) { const r = document.createRange(); r.selectNodeContents(n); ink.push(...[...r.getClientRects()].map(rel)); }
  });
  sec.querySelectorAll('[data-testid="hero-eyebrow"], [data-testid="hero-browse"], [data-testid="hero-lists"], [data-testid="hero-countdown"]').forEach((e) => ink.push(rel(e.getBoundingClientRect())));
  const T = rel(sec.querySelector('[data-testid="hero-text"]').getBoundingClientRect());
  const inkRight = Math.max(T.x, ...ink.map((r) => r.x + r.w));
  const stripTop = rel(sec.querySelector('[data-testid="home-b2b-badges"]').getBoundingClientRect()).y;
  const blocks = [...avoid, ...ink.map((r) => ({ x: r.x - 20, y: r.y - 20, w: r.w + 40, h: r.h + 40 }))];
  const W0 = el.offsetWidth, H0 = el.offsetHeight, maxX = sec.offsetWidth - 16;
  for (const s of scales) {
    const w = (W0 * Math.cos(ANG) + H0 * Math.sin(ANG)) * s, h = (W0 * Math.sin(ANG) + H0 * Math.cos(ANG)) * s;
    let best = null;
    for (let y = 16; y + h <= stripTop - 12; y += 8) {
      for (let x = inkRight + 20; x + w <= maxX; x += 8) {
        if (blocks.some((b) => hit({ x, y, w, h }, b))) continue;
        const tx = x + w, ty = y + h;
        const d = Math.min(...avoid.map((a) => Math.hypot(Math.max(a.x - tx, 0, tx - a.x - a.w), Math.max(a.y - ty, 0, ty - a.y - a.h))));
        if (!best || d < best.d) best = { d, s, left: x + w / 2 - W0 / 2, top: y + h / 2 - H0 / 2 };
      }
    }
    if (best) return best;
  }
  return false;
}

const NoteCopy = ({ text, refEl, p, testid }) => (
  <div ref={refEl} className="absolute left-0 top-0 w-max text-white" data-testid={testid} data-active={p ? "yes" : "no"}
    style={p ? { left: p.left, top: p.top, transform: `scale(${p.s}) rotate(-6deg)` } : { visibility: "hidden", transform: "rotate(-6deg)" }}>
    <p className="font-hand whitespace-pre-line text-[28px] leading-[1.12] drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)]">{text}</p>
    <svg width="120" height="90" viewBox="0 0 120 90" fill="none" className="ml-auto mt-1 block drop-shadow-[0_2px_4px_rgba(0,0,0,0.45)]" data-testid={`${testid}-arrow`}>
      <path d="M6 6 C 30 10, 70 22, 96 70" stroke="white" strokeWidth="3" strokeLinecap="round" />
      <path d="M80 64 L 98 74 L 100 54" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </div>
);

// Full note where it fits at a readable size; else the short version (mid-size desktops); else hidden.
function Note({ text, short, bg, variant, cdKey }) {
  const full = useRef(null);
  const sh = useRef(null);
  const [p, setP] = useState(null);
  useLayoutEffect(() => {
    const sec = full.current?.closest('[data-testid="home-hero"]');
    if (!sec) return undefined;
    const run = () => {
      const a = placeNote(full.current, bg, [1, 0.88, 0.78]);
      if (a) return setP({ ...a, v: "full" });
      const b = short ? placeNote(sh.current, bg, [1, 0.86, 0.74]) : false;
      return setP(b ? { ...b, v: "short" } : a === null ? null : false);
    };
    run();
    const img = sec.querySelector('[data-testid="hero-bg-image"]');
    img?.addEventListener("load", run);
    const ro = new ResizeObserver(run);
    ro.observe(sec);
    document.fonts?.ready.then(run);
    return () => { ro.disconnect(); img?.removeEventListener("load", run); };
  }, [text, short, bg, variant, cdKey]);
  return (
    <div aria-hidden="true" className={`pointer-events-none absolute inset-0 overflow-hidden ${pick(variant, "hidden lg:block", "block", "hidden", "hidden", "block")}`}
      data-testid="hero-note" data-placed={p ? "yes" : p === false ? "no-space" : "pending"} data-version={p?.v || ""}>
      <NoteCopy text={text} refEl={full} p={p?.v === "full" ? p : null} testid="hero-note-full" />
      {short && <NoteCopy text={short} refEl={sh} p={p?.v === "short" ? p : null} testid="hero-note-short" />}
    </div>
  );
}

function Strip({ items, variant }) {
  if (!items?.length) return null;
  const four = items.length === 4;
  return (
    <ul className={`relative grid border-t border-white/15 bg-[rgba(8,20,45,0.55)] backdrop-blur-sm ${pick(variant, `grid-cols-2 ${four ? "lg:grid-cols-4" : "lg:grid-cols-" + items.length}`, `grid-cols-${items.length}`, "grid-cols-2", "grid-cols-2")}`} data-testid="home-b2b-badges">
      {items.map((f, i) => {
        const I = HERO_ICON[f.icon] || Tag;
        const divider = pick(variant, `${i % 2 ? "border-l" : ""} ${i > 1 ? "border-t lg:border-t-0" : ""} ${i ? "lg:border-l" : ""}`, i ? "border-l" : "", `${i % 2 ? "border-l" : ""} ${i > 1 ? "border-t" : ""}`, `${i % 2 ? "border-l" : ""} ${i > 1 ? "border-t" : ""}`);
        return (
          <li key={i} className={`flex min-w-0 items-center gap-3 border-white/15 px-4 py-4 ${pick(variant, "sm:px-6 lg:py-5", "px-6 py-5", "px-6", "px-3 py-3")} ${divider}`} data-testid={`hero-feature-${i}`}>
            <I size={pick(variant, 26, 28, 26, 22)} strokeWidth={1.5} aria-hidden="true" className="shrink-0 text-white" />
            <span className="min-w-0"><span className={`block font-bold leading-tight text-white ${variant === "phone" ? "text-[13px]" : "text-sm sm:text-[15px]"}`}>{f.title}</span>
              {f.sub && <span className={`mt-0.5 block leading-snug text-slate-300 ${variant === "phone" ? "text-[12px]" : "text-xs sm:text-[13px]"}`}>{f.sub}</span>}</span>
          </li>);
      })}
    </ul>
  );
}

export function HomeHero({ hero, variant = "auto", onCountdownEnd, active = true, deferBg = false }) {
  const h = hero || {};
  const bg = h.bg || {};
  const secRef = useRef(null);
  const vid = useHeroVideo(deferBg ? {} : bg, variant, secRef, active);
  const lines = String(h.headline || "").split("\n");
  const keep = h.headline_phone_breaks !== false;
  const head = pick(variant, "text-[34px] leading-[1.08] sm:text-[44px] lg:text-[48px] lg:leading-[1.04] xl:text-[56px]", "text-[56px] leading-[1.04]", "text-[44px] leading-[1.06]", "text-[34px] leading-[1.08]", "text-[48px] leading-[1.04]");
  const btns = pick(variant, "flex-col sm:flex-row", "flex-row", "flex-row", "flex-col");
  const [cdOn, setCdOn] = useState(false);  // re-place the note when the countdown appears / goes
  const cd = h.countdown && !h.pending ? h.countdown : null;
  const cdEl = (pl) => cd?.placement === pl && <div className={pl === "above" ? "mb-5" : "mt-6"}><HeroCountdown cd={cd} onEnd={onCountdownEnd} onShow={setCdOn} /></div>;
  const btn = "inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full px-6 py-3 text-base font-semibold transition-colors duration-200 motion-reduce:transition-none";
  return (
    <section ref={secRef} className="relative overflow-hidden bg-[#08142D] text-white" data-testid="home-hero" data-bg={bg.kind || "navy"} data-variant={variant}>
      {!deferBg && <Bg bg={bg} variant={variant} vid={vid} />}
      <div className={`relative mx-auto max-w-6xl px-5 ${pick(variant, `${bg.kind === "video" ? "pb-20" : "pb-10"} pt-10 sm:pb-14 sm:pt-14 lg:pb-20 lg:pt-20`, "pb-20 pt-20", "pb-14 pt-14", bg.kind === "video" ? "pb-20 pt-10" : "pb-10 pt-10")}`}>
        <div className={`min-w-0 ${pick(variant, "max-w-[600px]", "max-w-[600px]", "max-w-[600px]", "")}`} data-testid="hero-text">
          {cdEl("above")}
          {h.eyebrow && <p className="w-fit text-xs font-bold uppercase tracking-[0.18em] text-[#B4D8FF] sm:text-sm" data-testid="hero-eyebrow">{h.eyebrow}</p>}
          <h1 className={`${head} mt-4 font-extrabold tracking-[-0.02em] text-white`} data-testid="hero-headline">{lines.map((ln, i) => (
            <span key={i} className={keep ? "block" : pick(variant, "inline md:block", "block", "block", "inline", "block")} data-testid={`hero-headline-line-${i}`}>{ln}{!keep && i < lines.length - 1 ? " " : ""}</span>))}</h1>
          {h.sub && <p className={`mt-5 ${pick(variant, "max-w-[560px] lg:max-w-[480px] xl:max-w-[560px]", "max-w-[560px]", "max-w-[560px]", "max-w-[560px]", "max-w-[480px]")} text-slate-200 ${variant === "phone" ? "text-base" : "text-base sm:text-lg"}`} data-testid="hero-sub">{h.sub}</p>}
          <div className={`mt-8 flex gap-3 ${btns}`}>
            {h.cta_label && h.cta_link && <Link to={h.cta_link} data-testid="hero-browse" className={`${btn} bg-[var(--brand-button)] text-[var(--brand-on-button)] hover:bg-[var(--brand-button-hover)]`}>{h.cta_label}<ArrowRight size={18} aria-hidden="true" /></Link>}
            {h.cta2_show && h.cta2_label && h.cta2_link && <Link to={h.cta2_link} data-testid="hero-lists" className={`${btn} border border-white/45 bg-white/10 text-white backdrop-blur-sm hover:bg-white/20`}>{h.cta2_icon && <ListChecks size={18} aria-hidden="true" />}{h.cta2_label}</Link>}
          </div>
          {cdEl("below")}
        </div>
      </div>
      {h.note_show && h.note && (bg.kind === "photo" || bg.kind === "video") && variant !== "tablet" && variant !== "phone" && <Note text={h.note} short={h.note_short} bg={bg} variant={variant} cdKey={cdOn} />}
      <div className="relative mx-auto max-w-6xl px-0 sm:px-5 lg:pb-8"><div className="overflow-hidden sm:rounded-2xl lg:border lg:border-white/10"><Strip items={h.features} variant={variant} /></div></div>
    </section>
  );
}
